<template>
  <section :class="$style.container">
    <header>
      <p :class="$style.title">送信セグメント条件設定</p>
    </header>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">家計・資産条件</p>
      <div :class="[$style.content__option, $style.option]">
        <p :class="$style.option__text">預貯金</p>
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.saving"
          :minOptions="priceRangeMinOptions"
          :maxOptions="priceRangeMaxOptions"
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <p :class="$style.option__text">本人年収</p>
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.annualIncome"
          :minOptions="priceRangeMinOptions"
          :maxOptions="priceRangeMaxOptions"
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <p :class="$style.option__text">世帯年収</p>
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.familyAnnualIncome"
          :minOptions="priceRangeMinOptions"
          :maxOptions="priceRangeMaxOptions"
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <p :class="$style.option__text">資産運用額</p>
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.investment"
          :minOptions="priceRangeMinOptions"
          :maxOptions="priceRangeMaxOptions"
        />
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">年齢</p>
      <div :class="[$style.content__option, $style.option]">
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.age"
          :minOptions="ageMinOptions"
          :maxOptions="ageMaxOptions"
        />
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">性別</p>
      <div :class="[$style.content__option, $style.option]">
        <ul :class="$style.radiobuttonSection">
          <li v-for="(option, index) in genderOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radiobutton"
              name="gender"
              v-model="value.gender"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">配偶者有無</p>
      <div :class="[$style.content__option, $style.option]">
        <ul :class="$style.radiobuttonSection">
          <li v-for="(option, index) in existenceOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radiobutton"
              name="hasPartner"
              v-model="value.hasPartner"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">子ども有無</p>
      <div :class="[$style.content__option, $style.option]">
        <ul :class="$style.radiobuttonSection">
          <li v-for="(option, index) in existenceOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radiobutton"
              name="hasChild"
              v-model="value.hasChild"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">1人目の子どもの年齢</p>
      <div :class="[$style.content__option, $style.option]">
        <DolphinRangeSelector
          :class="$style.rangeSelector"
          v-model="value.ageOfFirstChild"
          :minOptions="ageOfFirstChildMinOptions"
          :maxOptions="ageOfFirstChildMaxOptions"
        />
      </div>
    </div>

    <div :class="$style.section">
      <p :class="$style.sectionTitle">
        すでに同一企業のプランナーからアプローチされたユーザーに送るか（文言は考える必要あり）
      </p>
      <div :class="[$style.content__option, $style.option]">
        <ul :class="$style.radiobuttonSection">
          <li v-for="(option, index) in booleanOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radiobutton"
              name="shouldSendIfSameCorporationApproached"
              v-model="value.shouldSendIfSameCorporationApproached"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  ageMinOptions,
  ageMaxOptions,
  genderOptions,
  priceRangeMinOptions,
  priceRangeMaxOptions,
  existenceOptions,
  ageOfFirstChildMinOptions,
  ageOfFirstChildMaxOptions,
  booleanOptions,
} from './SettingsPageAttackListSenderEditorOptions'
import type { Range } from './SettingsPageAttackListSenderEditorOptions'

import DolphinRangeSelector from '@/components/molecules/DolphinRangeSelector.vue'
import DolphinRadioButtonInContainer from '@/components/molecules/DolphinRadioButtonInContainer.vue'
import {
  AttackListSenderSettingRuleExistence,
  AttackListSenderSettingRuleGender,
} from '@/types/schema.generated'

type FormData = {
  age: Range
  gender: AttackListSenderSettingRuleGender
  saving: Range
  annualIncome: Range
  investment: Range
  hasPartner: AttackListSenderSettingRuleExistence
  familyAnnualIncome: Range
  hasChild: AttackListSenderSettingRuleExistence
  ageOfFirstChild: Range
  shouldSendIfSameCorporationApproached: boolean
}

const props = defineProps<{
  modelValue: FormData
}>()

const emit = defineEmits<{
  (event: 'update:modelValue', value: FormData): void
}>()

const value = computed<FormData>({
  get() {
    return props.modelValue
  },
  set(newValue: FormData) {
    emit('update:modelValue', newValue)
  },
})
</script>

<style lang="scss" module>
.container {
  background-color: $color-background-base;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.title {
  font-size: 18px;
  font-weight: 700;
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
}

.option {
  display: flex;
  align-items: center;
  gap: 24px;

  &__text {
    font-size: 12px;
    text-align: right;
    width: 160px;
    font-weight: 700;
  }
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rangeSelector {
  display: flex;
  max-width: 608px;
  width: 100%;
}

.radiobuttonSection {
  display: flex;
  gap: 8px;
}

.radiobutton {
  width: 132px;
}
</style>
